import type { components } from "@/api-schema";
import { BrandLogo } from "@/components/brand-logo/brand-logo";
import { LocationDisplay } from "@/components/data-table/predefined/_helpers/relocation-order-location-display";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { FormCard } from "@/components/ui/form-card";
import { Skeleton } from "@/components/ui/skeleton";
import { SystemInfoFields } from "@/components/system-info-fields/system-info-fields";
import {
  RelocationOrderLocationCard,
  RelocationOrderLocationKind,
} from "@/modules/fit/internal/relocation-order/admin/_components/relocation-order-location-card";
import {
  RelocationOrderAdminFormFields,
  type RelocationOrderAdminFormSchemaType,
} from "@/modules/fit/internal/relocation-order/admin/_components/relocation-order-admin-form-fields";
import type { RelocationOrderAdminFormOptions } from "@/modules/fit/internal/relocation-order/admin/_hooks/use-relocation-order-admin-form-state";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { navigate } from "wouter/use-browser-location";

type Vehicle = components["schemas"]["Vehicle"];
type RelocationOrder = components["schemas"]["RelocationOrder"];

export type RelocationOrderAdminFormProps = {
  cardTitle: string;
  vehicle?: Vehicle;
  /** Only set when editing an existing relocation order. */
  relocationOrder?: RelocationOrder;
  form: UseFormReturn<RelocationOrderAdminFormSchemaType>;
  onSubmit: (values: RelocationOrderAdminFormSchemaType) => Promise<void>;
  isLoading: boolean;
  options: RelocationOrderAdminFormOptions;
};

export function RelocationOrderAdminForm({
  cardTitle,
  vehicle,
  relocationOrder,
  form,
  onSubmit,
  isLoading,
  options,
}: RelocationOrderAdminFormProps) {
  const { t } = useTranslation(["common", "fit"]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormCard title={cardTitle} titleHidden>
          <div className="space-y-6">
            <div className="flex flex-col gap-3">
              {vehicle && (
                <div className="flex flex-row items-center gap-3">
                  {vehicle.modelGeneration?.modelFamily?.brand && (
                    <BrandLogo brand={vehicle.modelGeneration.modelFamily.brand} />
                  )}
                  <div className="flex flex-col items-start">
                    {(vehicle.modelGeneration?.modelFamily?.brand?.name || vehicle.modelGeneration?.name) && (
                      <p className="font-semibold">
                        {[vehicle.modelGeneration?.modelFamily?.brand?.name, vehicle.modelGeneration?.name]
                          .filter(Boolean)
                          .join(" ")}
                      </p>
                    )}
                    {vehicle.currentBranch && (
                      <p>
                        {t("fit:CurrentBranch")}: {vehicle.currentBranch.name}
                      </p>
                    )}
                    {vehicle.vin && (
                      <p>
                        {t("fit:VIN")}: {vehicle.vin}
                      </p>
                    )}
                    {vehicle.color && (
                      <p>
                        {t("common:Color")}: {vehicle.color}
                      </p>
                    )}
                    {relocationOrder && !relocationOrder.currentBranch && relocationOrder.currentAddress && (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{t("fit:CurrentBranch")}:</span>
                        <LocationDisplay
                          address={relocationOrder.currentAddress}
                          className="text-foreground font-medium"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {relocationOrder && (
                <SystemInfoFields
                  uuid={relocationOrder.uuid}
                  createdAt={relocationOrder.createdAt}
                  updatedAt={relocationOrder.updatedAt}
                />
              )}
            </div>

            <RelocationOrderLocationCard
              kind={RelocationOrderLocationKind.Origin}
              form={form}
              branchOptions={options.mappedBranches}
              branchComboboxState={options.branchComboboxState}
              selectedBranch={options.selectedOriginBranch}
            />

            <RelocationOrderLocationCard
              kind={RelocationOrderLocationKind.Current}
              form={form}
              branchOptions={options.mappedBranches}
              branchComboboxState={options.branchComboboxState}
              selectedBranch={options.selectedCurrentBranch}
            />

            <RelocationOrderLocationCard
              kind={RelocationOrderLocationKind.Destination}
              form={form}
              branchOptions={options.mappedDestinationBranches}
              branchComboboxState={options.branchComboboxState}
              selectedBranch={options.selectedDestinationBranch}
            />

            <RelocationOrderAdminFormFields
              form={form}
              mappedTransportCompanies={options.mappedTransportCompanies}
              mappedTransportCompanyUsers={options.mappedTransportCompanyUsers}
              mappedOrderedByUsers={options.mappedOrderedByUsers}
              companyComboboxState={options.companyComboboxState}
              driverComboboxState={options.driverComboboxState}
              orderedByComboboxState={options.orderedByComboboxState}
              statuses={options.statuses}
            />
          </div>
        </FormCard>

        <div className="flex flex-wrap justify-end gap-4">
          <Button
            disabled={isLoading}
            onClick={() => navigate("/fit/relocation-order")}
            type="button"
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
          >
            {t("common:Cancel")}
          </Button>
          <Button disabled={isLoading || !form.formState.isValid} type="submit">
            {t("common:Save")}
          </Button>
        </div>
      </form>
    </Form>
  );
}

export function RelocationOrderAdminFormSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex flex-row items-center gap-3">
        <Skeleton className="h-20 w-20 rounded-full" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-5 w-56" />
        </div>
      </div>
      <div className="pt-6">
        <div className="max-w-xl space-y-8">
          <div className="flex flex-col space-y-3">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="flex flex-col space-y-3">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="flex flex-col space-y-3">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-20" />
            <Skeleton className="h-10 w-20" />
          </div>
        </div>
      </div>
    </div>
  );
}
